import axios, { isAxiosError } from 'axios';
import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';

export interface VerifiedImagesRequest {
  gpProductId: string;
  cpProductId: string;
  cpItemId: string;
}

export interface UpdateVerifiedImagesRequest extends VerifiedImagesRequest {
  imageUrlToAdd?: string[];
  imageUrlToRemove?: string[];
}

export interface TranslateVerifiedImagesRequest extends VerifiedImagesRequest {
  images: string[];
  language: string;
}

export interface VerifiedImagesResponse {
  success: boolean;
  verifiedImageUrls: string[];
  totalCount: number;
  message?: string;
}

export interface TranslateVerifiedImagesResponse {
  success: boolean;
  results: Array<{
    imageName: string;
    s3TranslatedUrl: string;
    success: boolean;
  }>;
  verifiedImageUrls: string[];
  totalCount: number;
  message?: string;
}

export interface PresignedUrlRequest extends VerifiedImagesRequest {
  fileName: string;
}

export interface PresignedUrlResponse {
  success: boolean;
  uploadUrl: string;
  s3Url: string;
  message?: string;
}

export interface DeleteVerifiedImagesRequest extends VerifiedImagesRequest {
  imageUrls: string[];
}

export interface DeleteVerifiedImagesResponse {
  success: boolean;
  message?: string;
}

export class VerifiedImagesClient {
  private readonly baseUrl: string;
  private readonly apiKey: string;

  constructor() {
    this.baseUrl = getAdminApiUrl(AdminApiDomain.SOURCING);
    this.apiKey = getConfig().apiKey;
  }

  async getVerifiedImages(request: VerifiedImagesRequest): Promise<VerifiedImagesResponse> {
    const url = `${this.baseUrl}/sourcing/verifiedImages`;
    const params = new URLSearchParams({
      gpProductId: request.gpProductId,
      cpProductId: request.cpProductId,
      cpItemId: request.cpItemId,
    });

    try {
      const response = await axios.get(`${url}?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to get verified images:', error);
      return {
        success: false,
        verifiedImageUrls: [],
        totalCount: 0,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async updateVerifiedImages(request: UpdateVerifiedImagesRequest): Promise<VerifiedImagesResponse> {
    const url = `${this.baseUrl}/sourcing/verifiedImages`;

    try {
      const response = await axios.patch(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to update verified images:', error);
      return {
        success: false,
        verifiedImageUrls: [],
        totalCount: 0,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async translateVerifiedImages(request: TranslateVerifiedImagesRequest): Promise<TranslateVerifiedImagesResponse> {
    const url = `${this.baseUrl}/sourcing/verifiedImages/translate`;

    try {
      const response = await axios.post(url, request, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to translate verified images:', error);
      return {
        success: false,
        results: [],
        verifiedImageUrls: [],
        totalCount: 0,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  async getPresignedUrl(request: PresignedUrlRequest): Promise<PresignedUrlResponse> {
    const url = `${this.baseUrl}/sourcing/verifiedImages/presignedUrl`;

    try {
      const response = await axios.post(url, request, {
        headers: { 'Content-Type': 'application/json', 'X-API-Key': this.apiKey },
      });

      return {
        success: true,
        uploadUrl: response.data.presignedUrl || response.data.uploadUrl,
        s3Url: response.data.s3Url,
      };
    } catch (error) {
      const errorMsg = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : 'Unknown error occurred';

      console.error('Failed to get presigned URL:', errorMsg);

      return {
        success: false,
        uploadUrl: '',
        s3Url: '',
        message: errorMsg,
      };
    }
  }

  async uploadToS3(presignedUrl: string, file: Blob): Promise<boolean> {
    try {
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return true;
    } catch (error) {
      console.error('Failed to upload to S3:', error);
      return false;
    }
  }

  async deleteVerifiedImages(request: DeleteVerifiedImagesRequest): Promise<DeleteVerifiedImagesResponse> {
    const url = `${this.baseUrl}/sourcing/verifiedImages`;

    try {
      const response = await axios.delete(url, {
        data: request,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Failed to delete verified images:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}

export const verifiedImagesClient = new VerifiedImagesClient();
